import React, { useEffect, useMemo, useState } from 'react';
import { BIO } from '@/constants/testIds';

// Resolve backend base URL with /api prefix (set at build time via .env).
const API_BASE = `${
  process.env.REACT_APP_BACKEND_URL || ''
}/api`.replace(/\/+$/, '');

// RFC-5322 compliant-enough check for a public capture form. We accept what
// HTML inputs accept (no exotic IDN handling) — the backend re-validates with
// Pydantic EmailStr anyway.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const readSourceFromUrl = () => {
  try {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    const src = (params.get('src') || '').toLowerCase().trim();
    return src || null;
  } catch (_err) {
    return null;
  }
};

const readCachedSource = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const cached = window.localStorage.getItem('sov_ingress_origin');
    return cached || null;
  } catch (_err) {
    return null;
  }
};

/**
 * EmailCaptureForm — minimal lead-capture form using shadcn design tokens
 * (bg-background / border-primary / text-foreground / text-muted-foreground).
 *
 * State machine: idle → submitting → success (or back to idle on error).
 * Source is sourced from `?src=...` first, falling back to the same
 * localStorage `sov_ingress_origin` cache used by the host-aware highlighting.
 * If neither is set, the backend default `'direct'` will be applied.
 */
const EmailCaptureForm = () => {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success'
  const [errorMessage, setErrorMessage] = useState('');

  // Re-evaluate source on each submit so the latest URL/cache state is used.
  const resolveSource = () => readSourceFromUrl() || readCachedSource() || 'direct';

  const isValidEmail = useMemo(() => EMAIL_REGEX.test(email.trim()), [email]);
  const showInlineError = touched && email.length > 0 && !isValidEmail;

  useEffect(() => {
    if (status === 'success') return;
    // Clearing the error message when the user resumes typing.
    if (errorMessage) setErrorMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (!isValidEmail || status === 'submitting') return;

    setStatus('submitting');
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE}/wingman/capture-lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          source: resolveSource(),
        }),
      });

      if (!response.ok) {
        let detail = `Capture failed (${response.status}).`;
        try {
          const body = await response.json();
          if (body && body.detail) detail = body.detail;
        } catch (_err) {
          /* ignore */
        }
        setStatus('idle');
        setErrorMessage(typeof detail === 'string' ? detail : 'Capture failed.');
        return;
      }

      setEmail('');
      setStatus('success');
    } catch (err) {
      setStatus('idle');
      setErrorMessage(`Network error: ${err.message || err}`);
    }
  };

  return (
    <section
      className="bg-background border border-primary rounded-lg p-5 transition-all duration-300 ease-in-out"
      data-testid={BIO.captureCard}
      aria-label="Email capture"
    >
      <h2
        className="text-foreground text-base font-semibold m-0"
        data-testid={BIO.captureTitle}
      >
        Save Your Self Time Energy &amp; Money
      </h2>
      <p className="text-muted-foreground text-[13px] mt-1 mb-3">
        Drop your email and we'll route the right resource to your inbox.
      </p>

      {status !== 'success' && (
        <form
          className="flex flex-col gap-2"
          onSubmit={handleSubmit}
          noValidate
        >
          <label className="sr-only" htmlFor="wingman-email">
            Email address
          </label>
          <input
            id="wingman-email"
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={status === 'submitting'}
            data-testid={BIO.captureInput}
            className="bg-background text-foreground border border-primary placeholder:text-muted-foreground rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all duration-300 ease-in-out"
          />
          {showInlineError && (
            <span
              className="text-destructive text-xs"
              data-testid={BIO.captureValidationError}
            >
              Enter a valid email address.
            </span>
          )}
          {errorMessage && (
            <span
              className="text-destructive text-xs"
              data-testid={BIO.captureServerError}
            >
              {errorMessage}
            </span>
          )}
          <button
            type="submit"
            disabled={status === 'submitting' || (touched && !isValidEmail)}
            data-testid={BIO.captureSubmit}
            className="bg-primary text-primary-foreground border border-primary rounded-md px-4 py-2 text-sm font-semibold transition-all duration-300 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'submitting' ? 'Anchoring…' : 'Send me the worksheet'}
          </button>
        </form>
      )}

      {status === 'success' && (
        <p
          className="text-foreground text-sm font-medium mt-1 transition-all duration-300 ease-in-out"
          role="status"
          aria-live="polite"
          data-testid={BIO.captureSuccess}
        >
          Sovereign system anchored. Check your inbox.
        </p>
      )}
    </section>
  );
};

export default EmailCaptureForm;
